import React, { useEffect, useRef, useCallback, useState } from "react";
import PropTypes from "kf-proptypes/widgets";

import { FORMAT_TYPES, ASSIST_FORMAT_TYPES } from "../util/constants.js";

import styles from "./toolbar.css";

Toolbar.propTypes = {
  toolbarButtons: PropTypes.objectOf(
    PropTypes.shape({
      buttons: PropTypes.arrayOf(
        PropTypes.shape({
          button: PropTypes.func,
          label: PropTypes.string,
          type: PropTypes.string
        })
      ),
      className: PropTypes.string,
      width: PropTypes.number,
      visibleIconLength: PropTypes.number
    })
  ),
  toolbarRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  inline: PropTypes.bool,
  className: PropTypes.string,
  flowId: PropTypes.string,
  renderBlock: PropTypes.func,
  iconSize: PropTypes.number,
  inlineLink: PropTypes.bool,
  toolbarRenderer: PropTypes.func,
  onClick: PropTypes.func,
  selectedFormat: PropTypes.shape({
    formatType: PropTypes.string,
    value: PropTypes.string
  })
};

export function Toolbar({
  toolbarButtons,
  toolbarRef,
  inline = false,
  className = "",
  flowId,
  renderBlock,
  iconSize = 12,
  inlineLink = false,
  toolbarRenderer,
  selectedFormat,
  onClick = () => {}
}) {
  const isListenerRegistered = useRef(false);
  const [editor, setEditor] = useState(
    (toolbarRef.current &&
      toolbarRef.current.getInstance &&
      toolbarRef.current.getInstance()) ||
      toolbarRef.current ||
      {}
  );

  const [toolbarState, setToolbarState] = useState({
    selectedFormats: {},
    rangeText: ""
  });

  useEffect(
    function gettingToolbarRef() {
      let toolbarValue =
        (toolbarRef.current &&
          toolbarRef.current.getInstance &&
          toolbarRef.current.getInstance?.()) ||
        toolbarRef.current;
      setEditor(toolbarValue);
    },
    [toolbarRef]
  );

  const handleTextChange = useCallback(
    function updateTextChangeHandler() {
      setTimeout(() => {
        const range = editor.getSelection(true);
        if (!range) return null;
        const formats = editor.getFormat(range);
        const rangeText = editor.getText(range);
        setToolbarState({
          selectedFormats: {
            ...formats,
            [ASSIST_FORMAT_TYPES[formats.list || formats.header]]: true
          },
          rangeText
        });
      });
    },
    [editor]
  );

  const handleSelectionChange = useCallback(
    function updateSelectionChangeHandler() {
      setTimeout(() => {
        const range = editor.getSelection?.();
        if (range && range.length > 0) {
          const formats = editor.getFormat(range);
          const rangeText = editor.getText(range);
          const selectedFormats = {
            ...formats,
            [ASSIST_FORMAT_TYPES[formats.list || formats.header]]: true
          };
          setToolbarState({
            selectedFormats,
            rangeText
          });
        } else if (range) {
          const formats = editor.getFormat(range);
          const rangeText = editor.getText(range);
          const selectedFormats = {
            ...formats,
            [ASSIST_FORMAT_TYPES[formats.list || formats.header]]: true
          };
          setToolbarState({
            selectedFormats,
            rangeText
          });
        }
      });
    },
    [editor]
  );

  const onEditorChange = useCallback(
    function updateEditorChangeHandler(type, newValue, _oldValue, source) {
      if (["selection-change", "text-change"].includes(type)) {
        if (type === "selection-change" && newValue) {
          handleSelectionChange();
        }
        if (type === "text-change") {
          handleTextChange();
        }
      }
    },
    [handleSelectionChange, handleTextChange]
  );

  useEffect(function updateSelectionHandler() {
    if (!editor) return;
    handleSelectionChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    function registerListeners() {
      if (!isListenerRegistered.current && editor && editor.on) {
        editor.on("editor-change", onEditorChange);
        isListenerRegistered.current = true;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor]
  );

  useEffect(
    function onFormatSelected() {
      if (selectedFormat && selectedFormat.formatType && selectedFormat.value) {
        setTimeout(() => {
          let { formatType, value } = selectedFormat;
          format(formatType, value);

          const range = editor && editor.getSelection(true);
          if (!range) return null;
          const formats = editor.getFormat(range);
          const rangeText = editor && editor.getText(range);
          setToolbarState({
            selectedFormats: { ...formats, [formatType]: value },
            rangeText
          });
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFormat]
  );

  function format(type, value) {
    if (type === FORMAT_TYPES.LINK) {
      const range = editor.getSelection(true);
      if (!range.length) {
        const text = value.text || "";
        editor.insertText(range, text);
        editor.setSelection(
          editor.getSelection(true).index - text.length,
          text.length
        );
      }
    }
    editor.format?.(type, value);
  }

  function removeFormat() {
    editor.removeFormat(editor.getSelection(true));
  }

  function onFormat(type, value) {
    if (type === FORMAT_TYPES.ERASE) {
      removeFormat();
    } else {
      format(type, value);
    }
  }

  function insertBlock(type, data) {
    const editor = toolbarRef.current;
    if (editor.insertBlock) editor.insertBlock(type, data);
    else if (renderBlock) renderBlock(type, data);
  }

  return (
    <div
      className={`${styles.toolbar} ${className} toolbar`}
      id="editor-toolbar"
      tabIndex="-1"
      onClick={onClick}
    >
      {toolbarRenderer
        ? toolbarRenderer({
            activeClass: styles.active,
            selectedFormats: toolbarState.selectedFormats,
            onFormat,
            insertBlock,
            rangeText: toolbarState.rangeText
          })
        : toolbarButtons &&
          toolbarButtons.map((item, index) => {
            const Button = item.button;
            return (
              <div
                key={index}
                className={styles.toolbarButton}
                onMouseDown={(e) =>
                  item.type === FORMAT_TYPES.LINK || !item?.type
                    ? ""
                    : e.preventDefault()
                }
              >
                <Button
                  activeClass={styles.active}
                  selectedFormats={toolbarState.selectedFormats}
                  toggleFormat={onFormat}
                  insertBlock={insertBlock}
                  rangeText={toolbarState.rangeText}
                  inline={inline}
                  flowId={flowId}
                  size={iconSize}
                  inlineLink={inlineLink}
                />
              </div>
            );
          })}
    </div>
  );
}
